import type { AsyncData, AsyncDataOptions, NuxtApp } from '#app'
import { computed, getCurrentInstance, getCurrentScope, isRefOrGetter, nextTick, onScopeDispose, onUnmounted, reactive, shallowRef, toValue, useApi, useAsyncData, useNuxtApp, useQueryCache, watch, type ApiError, type OpenapiError } from '#imports'
import type { UseQueryFunctionOptions, UseQueryHandler, UseQueryOptions, UseQueryReturn, UseQuerySharedOptions, UseQueryUrl } from '../../types'
import { omit, once } from 'es-toolkit'
import type { Merge } from 'type-fest'
import { holdPrefetch, isPrefetching, prefetchResult, takePrefetch } from '../../utils/prefetch'

export function useQuery<TResponse, TError = ApiError<TResponse> | OpenapiError<TResponse> | Error | null>(requestOrHandler: UseQueryUrl, queryOptions: UseQueryOptions<TResponse>): UseQueryReturn<TResponse, TError>
export function useQuery<TResponse, TError = ApiError<TResponse> | OpenapiError<TResponse> | Error | null>(requestOrHandler: UseQueryHandler<TResponse>, queryOptions: UseQueryFunctionOptions<TResponse>): UseQueryReturn<TResponse, TError>
export function useQuery<TResponse, TError = ApiError<TResponse> | OpenapiError<TResponse> | Error | null>(requestOrHandler: unknown, queryOptions: unknown): UseQueryReturn<TResponse, TError> {
    // Taken out rather than forwarded, so a JS caller cannot smuggle one in — see `UseQueryOptions`.
    const { key: asyncDataKey, enabled: enabledUser = true, dedupe = 'defer', timeout: _unsupportedTimeout, queryCache = {
        ttl: 10,
    }, ...queryOptionsUser } = queryOptions as UseQuerySharedOptions & AsyncDataOptions<TResponse>

    const ASYNC_DATA_OPTION_KEYS = ['deep', 'default', 'getCachedData', 'immediate', 'lazy', 'pick', 'server', 'transform', 'watch'] as const
    const cacheManager = useQueryCache<TResponse>()
    const nuxtApp = useNuxtApp()
    const prefetching = isPrefetching(nuxtApp)

    // `enabled` gates only this consumer's initial fetch. It must not gate the
    // shared handler/transform below: Nuxt binds those to whichever consumer
    // initialises the key first, so a disabled initialiser would veto enabled
    // siblings on the same key. A consumer that must stay silent while disabled
    // guards its own handler instead (e.g. useGameSection's `if (!isActive) return`),
    // and the transform leaves that skip uncached.
    const immediate = toValue(enabledUser) && (queryOptionsUser.immediate ?? true)

    // A consumer created while the key's fetch is in flight already shares that result.
    // Nuxt would still snapshot `cachedData: undefined` for it and, when mounting during
    // hydration, run the deferred initial fetch after the first request landed — past the
    // cache, so the same request went out twice (FRO-37).
    const joinsInFlightFetch = import.meta.client
        && !!nuxtApp._asyncData[toValue(asyncDataKey)]?._init
        && !!nuxtApp._asyncDataPromises[toValue(asyncDataKey)]

    const asyncDataOptions: AsyncDataOptions<TResponse> = {
        ...queryOptionsUser,
        immediate: immediate && !joinsInFlightFetch,
        dedupe,
    }

    if (queryCache) {
        asyncDataOptions.getCachedData = cacheManager.getCachedDataHandler

        asyncDataOptions.transform = async (input) => {
            const preparedInput = queryOptionsUser.transform
                ? await queryOptionsUser.transform(input)
                : input

            if (preparedInput === undefined && !toValue(enabledUser)) return preparedInput

            // A 204 resolves `undefined`, which the cache would read as a miss; `null` is the answer.
            return cacheManager.transformHandler(toValue(asyncDataKey), preparedInput ?? null as TResponse, queryCache.ttl)
        }
    }

    // Async data handler
    const apiOptions: Record<string, any> | undefined = typeof requestOrHandler === 'function' ? undefined : reactive(omit(queryOptionsUser, ASYNC_DATA_OPTION_KEYS))

    const getApi = once(() => useApi({
        enableCookieForwarding: (queryOptionsUser as any).enableCookieForwarding,
        enableLegacySupport: (queryOptionsUser as any).enableLegacySupport,
        service: (queryOptionsUser as any).service,
    }))

    // Nuxt's per-key signal: aborted when the key's LAST subscriber disposes.
    function asyncDataHandler(nuxtApp?: NuxtApp, context?: { signal?: AbortSignal }) {
        const prefetched = nuxtApp && takePrefetch(nuxtApp, toValue(asyncDataKey))

        if (prefetched) return prefetched as Promise<TResponse>

        if (typeof requestOrHandler === 'function') {
            return requestOrHandler({ nuxtApp, signal: context?.signal })
        }
        else {
            // Fresh options per run: the shared reactive one would leak a later run's signal.
            return getApi()(toValue(requestOrHandler as UseQueryUrl), { ...apiOptions, signal: context?.signal })
        }
    }

    /** Inside a prefetch: holds the request for the page's own query, and stands in with a settled result (an earlier prefetch's, if any) that registers nothing. */
    function prefetchQuery(): AsyncData<TResponse, TError> {
        const key = toValue(asyncDataKey)
        const runsHere = import.meta.client || asyncDataOptions.server !== false
        const result = prefetchResult(nuxtApp, key) as TResponse | undefined

        if (immediate && runsHere && result === undefined && !nuxtApp._asyncDataPromises[key] && cacheManager.get(key) === undefined) {
            holdPrefetch(nuxtApp, key, signal => asyncDataHandler(nuxtApp, { signal }))
        }

        const settled = Promise.resolve()

        return {
            data: shallowRef(result ?? queryOptionsUser.default?.()),
            error: shallowRef(undefined),
            status: shallowRef(result === undefined ? 'idle' : 'success'),
            pending: shallowRef(false),
            refresh: () => settled,
            execute: () => settled,
            clear: () => {},
            then: (onfulfilled?: any, onrejected?: any) => settled.then(onfulfilled, onrejected),
        } as unknown as AsyncData<TResponse, TError>
    }

    const query = prefetching
        ? prefetchQuery()
        : useAsyncData<TResponse, TError>(asyncDataKey, asyncDataHandler, asyncDataOptions) as AsyncData<TResponse, TError>

    // `idle` counts as loading, but a disabled query stays `idle` forever — gate on
    // `enabled` so it doesn't read as a perpetual skeleton.
    const isLoading = computed(() => toValue(enabledUser) && (query.status.value === 'idle' || query.status.value === 'pending'))

    // Snapshot of query's data properties + isLoading, without then/catch/finally.
    // enrich spreads from this so isLoading is included without putting reactive
    // objects onto the native Promise returned by useAsyncData.
    const queryData = {
        ...omit(query as any, ['then', 'catch', 'finally']),
        isLoading,
        enrich,
        bind,
    }

    /**
     * Extends this query with additional reactive properties, returning a merged
     * object that includes both the query's own properties (data, error, status,
     * refresh, etc.) and the supplied properties.
     *
     * The returned object is a thenable that chains on the original query promise,
     * so `await`-ing it waits for the query to resolve. Properties are also
     * accessible synchronously (no await needed for template refs).
     *
     * Use `enrich` when the composable's consumer needs access to query internals
     * (e.g. `data`, `refresh`, `status`) alongside derived state.
     *
     * **Why this exists:** In Nuxt SSR, reactive effects (computed, watch) created
     * from query state can outlive the request if returned in a plain object.
     * `enrich` chains derived state onto the query's promise, keeping everything
     * in the same lifecycle so Nuxt can track and clean up the async operation.
     * Returning a plain `{ ... }` object instead breaks this chain and causes
     * memory leaks on the server.
     *
     * @example
     * ```ts
     * const useGameData = () => {
     *     const query = useQuery(handler, { key: 'game' })
     *     const formatted = computed(() => query.data.value?.name)
     *     // Returns: { data, error, status, refresh, ..., formatted }
     *     return query.enrich({ formatted })
     * }
     * ```
     */
    function enrich<TProperties extends Record<string, any>>(properties: TProperties): Merge<typeof queryData, TProperties> {
        const plain = {
            ...queryData,
            ...properties,
        }

        return {
            ...plain,
            then(onfulfilled?: any, onrejected?: any) {
                return query.then(() => plain).then(onfulfilled, onrejected)
            },
            catch(onrejected?: any) {
                return query.then(() => plain).catch(onrejected)
            },
            finally(onfinally?: any) {
                return query.then(() => plain).finally(onfinally)
            },
        } as unknown as Merge<typeof queryData, TProperties>
    }

    /**
     * Binds reactive properties to this query's lifecycle **without** including
     * the query's own properties (data, error, status, refresh, etc.) in the
     * return value.
     *
     * Like `enrich`, the returned object is a thenable chained on the query
     * promise — but it only contains the properties you pass in.
     *
     * Use `bind` when a composable creates or wraps a query internally but its
     * public API should not expose query internals. The composable can still
     * read `query.data`, `query.error`, etc. internally via the local variable.
     *
     * **Why this exists:** Same SSR memory-leak concern as `enrich`. Any
     * composable that instantiates a `useQuery` — directly or transitively —
     * must return through `enrich` or `bind` so derived reactive state stays
     * chained to the query's promise lifecycle. `bind` is the lighter option
     * when consumers don't need `data`/`refresh`/`status`.
     *
     * @example
     * ```ts
     * const useMoneyLimits = () => {
     *     const configsQuery = useConfigs()          // wraps useQuery internally
     *     const limits = computed(() => configsQuery.data.value?.limits)
     *     const isValid = computed(() => limits.value?.length > 0)
     *     // Returns: { limits, isValid } — no query data/error/status
     *     return configsQuery.bind({ limits, isValid })
     * }
     * ```
     */
    function bind<TProperties extends Record<string, any>>(properties: TProperties): TProperties & Promise<TProperties> {
        const plain = { ...properties }

        return {
            ...plain,
            then(onfulfilled?: any, onrejected?: any) {
                return query.then(() => plain).then(onfulfilled, onrejected)
            },
            catch(onrejected?: any) {
                return query.then(() => plain).catch(onrejected)
            },
            finally(onfinally?: any) {
                return query.then(() => plain).finally(onfinally)
            },
        } as unknown as TProperties & Promise<TProperties>
    }

    if (import.meta.client && !prefetching) {
        const instance = getCurrentInstance()
        const scope = getCurrentScope()

        if (isRefOrGetter(enabledUser)) {
            const stop = watch(enabledUser, async (enabledUser) => {
                if (!enabledUser) return

                await nextTick()

                // Transitioning enabled→true must not force a cache-bypassing
                // network request when a still-fresh cache entry already exists.
                // refresh() bypasses getCachedData by design, so a freshly-cached
                // key — e.g. one populated by a sibling consumer that mounted
                // moments earlier — would otherwise refetch needlessly. Serve from
                // the cache while it's valid; only hit the network once it expires.
                if (cacheManager.get(toValue(asyncDataKey)) !== undefined) return

                await query.refresh({ dedupe: 'defer' })
            })

            if (instance) {
                onUnmounted(stop)
            }
            else if (scope) {
                onScopeDispose(stop)
            }
        }

        // Self-heal: Nuxt strands an enabled query at `idle`, with nothing scheduled to
        // execute it, when useQuery runs after an `await` in setup — still true on 4.5.1,
        // whose asyncData fixes only cover queries created synchronously.
        // The first refresh may dedupe onto a detached in-flight run, hence the retry.
        if (immediate) {
            const stopHeal = watch(
                () => [toValue(asyncDataKey), query.status.value],
                async () => {
                    if (!toValue(enabledUser) || query.status.value !== 'idle') return

                    await nextTick()

                    for (let attempt = 0; attempt < 3 && toValue(enabledUser) && query.status.value === 'idle'; attempt++) {
                        await query.refresh({ dedupe: 'defer' })
                        await nextTick()
                    }
                },
                { immediate: true, flush: 'post' },
            )

            if (instance) {
                onUnmounted(stopHeal)
            }
            else if (scope) {
                onScopeDispose(stopHeal)
            }
        }
    }

    return {
        ...queryData,
        then(onfulfilled?: any, onrejected?: any) {
            return query.then(() => queryData).then(onfulfilled, onrejected)
        },
        catch(onrejected?: any) {
            return query.then(() => queryData).catch(onrejected)
        },
        finally(onfinally?: any) {
            return query.then(() => queryData).finally(onfinally)
        },
    } as unknown as UseQueryReturn<TResponse, TError>
}
